/**
 * useTouchGesture Hook
 * 
 * Handles touch gesture detection (swipe left, swipe right).
 * Returns event handlers to attach to elements.
 * 
 * @param options - Configuration for gesture detection
 * @param options.threshold - Minimum distance (px) to trigger swipe (default: 50)
 * @param options.onSwipeRight - Callback for right swipe
 * @param options.onSwipeLeft - Callback for left swipe
 * 
 * @example
 * const touchHandlers = useTouchGesture({
 *   threshold: 50,
 *   onSwipeRight: closeMenu
 * });
 * 
 * <div {...touchHandlers}>Content</div>
 */

import { useRef, useCallback } from "react";
import { TouchGestureOptions } from "../types/navigation";

export function useTouchGesture({
    threshold = 50,
    onSwipeRight,
    onSwipeLeft,
}: TouchGestureOptions) {
    const touchStartX = useRef<number>(0);
    const touchCurrentX = useRef<number>(0);

    const handleTouchStart = useCallback((e: React.TouchEvent) => {
        touchStartX.current = e.touches[0].clientX;
        touchCurrentX.current = e.touches[0].clientX;
    }, []);

    const handleTouchMove = useCallback((e: React.TouchEvent) => {
        touchCurrentX.current = e.touches[0].clientX;
    }, []);

    const handleTouchEnd = useCallback(() => {
        const diff = touchCurrentX.current - touchStartX.current;

        if (diff > threshold && onSwipeRight) {
            onSwipeRight();
        } else if (diff < -threshold && onSwipeLeft) {
            onSwipeLeft();
        }

        touchStartX.current = 0;
        touchCurrentX.current = 0;
    }, [threshold, onSwipeRight, onSwipeLeft]);

    return {
        onTouchStart: handleTouchStart,
        onTouchMove: handleTouchMove,
        onTouchEnd: handleTouchEnd,
    };
}
